<script setup lang="ts">
import { defineProps, computed } from "vue";
import { MonthDayReport } from "@/types/calculators";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  dayReport: MonthDayReport;
  cardNumber: number;
  isCardSelected: boolean;
  isCardMuted: boolean;
}>();

const { t } = useI18n();

const cardsValues = computed(() => {
  let values = {
    card: "",
    cardBadge: {
      classes: "",
      content: "",
    },
  };

  switch (props.dayReport.tier) {
    case "golden":
      values["card"] = "bg-[#FFFDF7] border-gold/60";
      values["cardBadge"] = {
        classes: "bg-gold text-white",
        content: `★ ${props.dayReport.combined_index}%`,
      };
      break;
    case "favorable":
      values["card"] = "bg-[#F7FCF8] border-success/40";
      values["cardBadge"] = {
        classes: "bg-success text-white",
        content: `✓ ${props.dayReport.combined_index}%`,
      };
      break;
    case "personal_routine":
      values["card"] = "bg-white border-divider";
      values["cardBadge"] = {
        classes: "bg-beige text-active border border-active/2",
        content: `Рутина`,
      };
      break;
    case "personal_clash":
      values["card"] = "bg-[#FDF6F6] border-error/40";
      values["cardBadge"] = {
        classes: "bg-error text-white",
        content: `冲 Пробій`,
      };
  }

  return values;
});

const structure = computed<{ content: string; classes: string }>(() => {
  if (props.dayReport.tier == "personal_clash") {
    return {
      content: `✕ ${props.dayReport.personal_status.warnings[0]}` ?? "",
      classes: "text-error",
    };
  }
  if (props.dayReport.personal_status.structures.length > 0) {
    return {
      content: props.dayReport.personal_status.structures[0] ?? "",
      classes: "text-[#9A6E1E]",
    };
  }
  if (props.dayReport.personal_status.stars.length > 0) {
    return {
      content: props.dayReport.personal_status.stars[0] ?? "",
      classes: "text-[#9A6E1E]",
    };
  }
  return {
    content: `${props.dayReport.personal_status.ten_god_stem} / ${props.dayReport.personal_status.ten_god_branch}`,
    classes: "text-muted",
  };
});

const doing = computed<{ content: string; isBad: boolean }>(() => {
  if (props.dayReport.yi_preview.length > 0) {
    return {
      content: `宜: ${props.dayReport.yi_preview[0].name} (${props.dayReport.yi_preview[0].ua})`,
      isBad: false,
    };
  }
  if (props.dayReport.ji_preview.length > 0) {
    return {
      content: `忌: ${props.dayReport.ji_preview[0].name}`,
      isBad: true,
    };
  }
  return {
    content: "",
    isBad: true,
  };
});
</script>

<template>
  <div
    class="p-1.5 sm:p-3 rounded-lg sm:rounded-xl border transition-all cursor-pointer flex flex-col justify-between min-h-[68px] sm:min-h-[155px] relative hover:shadow-md"
    :class="[
      cardsValues.card,
      isCardSelected ? 'ring-2 ring-active ring-offset-1' : '',
      isCardMuted ? 'opacity-25 grayscale pointer-events-none' : '',
    ]"
  >
    <div>
      <div class="flex items-start justify-between gap-0.5">
        <span class="text-xs sm:text-lg font-bold leading-none text-brown">
          {{ cardNumber }}
        </span>
        <span
          class="px-1 py-0.5 rounded text-[8px] sm:text-[10px] font-bold leading-none"
          :class="cardsValues.cardBadge.classes"
        >
          <span class="hidden sm:inline">
            {{ cardsValues.cardBadge.content }}
          </span>
        </span>
      </div>
      <div
        class="mt-1 sm:mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white/80 px-1 sm:px-2 py-0.5 sm:py-1 rounded border border-divider/70 text-center sm:text-left"
      >
        <div
          class="font-caudex font-bold text-[11px] sm:text-sm text-brown leading-tight"
        >
          {{ dayReport.pillars.day }}
        </div>
        <span
          class="text-[9px] sm:text-[11px] font-bold leading-tight"
          :class="
            dayReport.officer_12.is_auspicious
              ? 'text-success'
              : 'text-alternative-gray'
          "
        >
          {{ dayReport.officer_12.name }}
        </span>
      </div>
      <div class="hidden lg:block mt-1.5">
        <div
          class="text-[10px] font-semibold truncate"
          :class="structure.classes"
        >
          {{ structure.content }}
        </div>
      </div>
    </div>
    <div class="hidden sm:block mt-2 pt-1.5 border-t border-divider/60">
      <div
        class="text-[10px] font-medium truncate"
        :class="doing.isBad ? 'text-error' : 'text-success'"
      >
        {{ doing.content }}
      </div>
      <div
        class="flex items-center justify-between text-[10px] text-muted mt-0.5"
      >
        <i18n-t keypath="calculators.personal-date.day-rang" tag="span">
          <template #rang>
            <strong>{{ dayReport.general_status.rank_code }}</strong>
          </template>
        </i18n-t>
        <span class="text-active font-semibold">
          {{ t("calculators.personal-date.details") }}
        </span>
      </div>
    </div>
  </div>
</template>
